import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useFrontendAuth } from '../hooks/useAuth';
import { useProtectedQuery } from '../hooks/useProtectedQuery';
import { ensureRateReminder, getPendingRatings } from '../services/api.shop';
import { PendingRatingDto } from '../types';
import RatingPopup from './RatingPopup';
import { useLoginPrompt } from '../loginPrompts/loginPromptContext';

const DISMISS_KEY = (orderId: number) => `ratingPromptDismissed:${orderId}`;

function wasDismissedThisSession(orderId: number): boolean {
    try {
        return sessionStorage.getItem(DISMISS_KEY(orderId)) === '1';
    } catch {
        return false;
    }
}

function markDismissedThisSession(orderId: number) {
    try {
        sessionStorage.setItem(DISMISS_KEY(orderId), '1');
    } catch {
        /* ignore */
    }
}

/**
 * Shows RatingPopup for delivered orders that still need a rating.
 * - Auto-opens on shop load (unless skipped this session).
 * - Notification link /home?rateOrder={id} re-opens the same popup.
 * - Skip/close creates the rate-reminder notification.
 */
const PendingRatingPrompt = () => {
    const { isAuthenticated, isAuthReady } = useFrontendAuth();
    const { grantedId, reportWaiting, claim, requestInterrupt } = useLoginPrompt();
    const [searchParams, setSearchParams] = useSearchParams();
    const queryClient = useQueryClient();
    const rateOrderParam = searchParams.get('rateOrder');
    const rateOrderId =
        rateOrderParam && !Number.isNaN(Number(rateOrderParam))
            ? Number(rateOrderParam)
            : null;

    const { data: pending = [], isFetched } = useProtectedQuery<PendingRatingDto[]>({
        queryKey: ['pendingRatings'],
        queryFn: getPendingRatings,
        enabled: isAuthReady && isAuthenticated,
        staleTime: 30 * 1000,
    });

    const [active, setActive] = useState<PendingRatingDto | null>(null);
    const [deepLink, setDeepLink] = useState<PendingRatingDto | null>(null);
    /** True while we are processing a ?rateOrder= open (prevents double-fire before URL clears). */
    const processingQueryRef = useRef(false);
    const eligible =
        isAuthReady &&
        isAuthenticated &&
        isFetched &&
        pending.some((item) => !wasDismissedThisSession(item.orderId));

    useEffect(() => {
        reportWaiting('rating', eligible);
    }, [eligible, reportWaiting]);

    // Allow the same notification link to reopen after the popup is closed
    useEffect(() => {
        if (!rateOrderParam && !active) {
            processingQueryRef.current = false;
        }
    }, [rateOrderParam, active]);

    useEffect(() => {
        if (!isAuthReady || !isAuthenticated || !isFetched) return;
        if (!rateOrderParam || rateOrderId == null || processingQueryRef.current) return;
        const match = pending.find((item) => item.orderId === rateOrderId);
        processingQueryRef.current = true;
        if (match) {
            setDeepLink(match);
            requestInterrupt('rating');
        }
        const next = new URLSearchParams(searchParams);
        next.delete('rateOrder');
        setSearchParams(next, { replace: true });
    }, [
        isAuthReady,
        isAuthenticated,
        isFetched,
        pending,
        rateOrderId,
        rateOrderParam,
        requestInterrupt,
        searchParams,
        setSearchParams,
    ]);

    useEffect(() => {
        if (grantedId !== 'rating' || active) return;
        const next = deepLink ?? pending.find((item) => !wasDismissedThisSession(item.orderId));
        if (!next) return;
        claim('rating');
        setActive(next);
        setDeepLink(null);
    }, [grantedId, active, deepLink, pending, claim]);

    const handleClose = () => setActive(null);

    const handleSkipOrClose = async () => {
        if (!active) return;
        const orderId = active.orderId;
        markDismissedThisSession(orderId);
        try {
            await ensureRateReminder(orderId);
            await queryClient.invalidateQueries({ queryKey: ['notifications'] });
        } catch (e) {
            console.error('Failed to create rate reminder', e);
        }
    };

    const handleSubmitted = async () => {
        if (active) {
            markDismissedThisSession(active.orderId);
        }
        await queryClient.invalidateQueries({ queryKey: ['pendingRatings'] });
        await queryClient.invalidateQueries({ queryKey: ['notifications'] });
        await queryClient.invalidateQueries({ queryKey: ['sellerSummary'] });
    };

    if (!isAuthenticated || !active) return null;

    return (
        <RatingPopup
            isOpen={!!active}
            onClose={handleClose}
            orderId={active.orderId}
            productTitle={active.productTitle}
            productImageUrl={active.productImageUrl ?? undefined}
            onSkipOrClose={handleSkipOrClose}
            onSubmitted={handleSubmitted}
        />
    );
};

export default PendingRatingPrompt;
